import { useEffect, useRef, useState, memo } from "react";
import useAppStatus from "shared/store/appStatusStore";
import { ConstraintsChevronIcon } from "shared/assets/svg/ConstraintsChevronIcon";
import commonStyles from "pages/assistant/components/artifacts/components/fineco-portfolio/finecoPortfolio.common.module.scss";
import { debounce } from "utils/misc";
import useThreadsStore from "shared/store/threadsStore";

type SectionProps = {
  section: string;
  label: string;
  children: React.ReactNode;
  toggleSection: (section: string) => void;
  openSections: Record<string, boolean>;
  reportData: any;
  showFoldingTitle: boolean;
};

const SectionComponent: React.FC<SectionProps> = ({
  children,
  section,
  toggleSection,
  openSections,
  reportData,
  label,
  showFoldingTitle,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number>(0);
  const [isVisible, setIsVisible] = useState<boolean>(openSections[section]);
  const appStatus = useAppStatus((state) => state.appStatus);
  const stories = useThreadsStore((state) => state.stories);

  const sectionKeys = Object.keys(openSections);
  const index = sectionKeys.indexOf(section);
  const isPreviousSectionOpen =
    index > 0 && openSections[sectionKeys[index - 1]];

  useEffect(() => {
    const updateContentHeight = () => {
      if (contentRef.current) {
        setContentHeight(contentRef.current.scrollHeight);
      }
    };

    updateContentHeight();
    setTimeout(updateContentHeight, 100);

    const debouncedResize = debounce(updateContentHeight, 150);
    window.addEventListener("resize", debouncedResize);

    return () => window.removeEventListener("resize", debouncedResize);
  }, [reportData, appStatus, openSections, stories]);

  useEffect(() => {
    if (openSections[section]) {
      setIsVisible(true);
    } else {
      const timeout = setTimeout(() => {
        setIsVisible(false);
      }, 500); // match the CSS transition duration
      return () => clearTimeout(timeout);
    }
  }, [openSections[section]]);

  return (
    <>
      {showFoldingTitle && (
        <div
          onClick={() => toggleSection(section)}
          className={`${commonStyles.sectionFoldingContainer} ${
            isPreviousSectionOpen
              ? commonStyles.sectionFoldingContainerOpen
              : ""
          }`}
        >
          <h3 style={{ marginBottom: 0, marginLeft: 5 }}>{label}</h3>
          <ConstraintsChevronIcon
            className={commonStyles.sectionChevron}
            isExpanded={openSections[section]}
          />
        </div>
      )}
      <div
        ref={contentRef}
        className="transition-max-height overflow-hidden duration-500 ease-in-out"
        style={{
          maxHeight: openSections[section] ? `${contentHeight}px` : "0",
        }}
      >
        {isVisible && children}
      </div>
    </>
  );
};

function areEqual(prevProps: SectionProps, nextProps: SectionProps) {
  return (
    prevProps.section === nextProps.section &&
    prevProps.label === nextProps.label &&
    prevProps.children === nextProps.children &&
    prevProps.showFoldingTitle === nextProps.showFoldingTitle &&
    prevProps.reportData === nextProps.reportData &&
    prevProps.openSections[prevProps.section] ===
      nextProps.openSections[nextProps.section]
  );
}

export const Section = memo(SectionComponent, areEqual);
